/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Per-serving nutrition, on the recipe screen.
 *
 * ── WHAT THIS COMPONENT IS CAREFUL ABOUT ────────────────────────────────────
 * Three things, in order of how badly they go wrong:
 *
 *   1. It says PER SERVING in the heading, always. The single most likely
 *      misreading of eight numbers next to a servings stepper is that they
 *      belong to the pot rather than the plate — and the stepper is directly
 *      above this panel on the same screen. Scaling to twelve servings does not
 *      change a single figure here, which looks like a bug until the heading
 *      explains that it isn't.
 *
 *   2. It says ESTIMATE, and it does not hide the estimate's own caveat. The
 *      `note` the model returns is usually about how much salt was assumed,
 *      which for the household reading this panel because of a blood-pressure
 *      condition is more useful than the sodium figure itself.
 *
 *   3. It renders NOTHING — not a heading, not a placeholder — when the
 *      household is nutrition-silent. See NUTRITION_SILENT_CONDITIONS in
 *      server/medical.ts. "Nutrition hidden" is still a sentence about calories.
 */

import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { RecipesApi } from "../../api/endpoints.ts";
import type { NutritionPanel as Panel, NutritionStatus } from "../../api/types.ts";
import { usePrefs } from "../../i18n/index.tsx";
import { Spinner } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";

/**
 * Field order and units. Mirrors NUTRITION_FIELDS in server/nutrition.ts.
 *
 * The labels are i18n keys rather than literals; the units are not, because
 * "g" and "mg" are the same in both languages this app speaks and inventing
 * translatable keys for them would suggest otherwise.
 *
 * Values arrive already rounded by the server's normaliser — whole numbers for
 * kcal and mg, one decimal for grams — so there is no rounding decision here
 * and none for the iOS panel either. Both just append the unit.
 */
const FIELDS: ReadonlyArray<{
  key: keyof Panel["per_serving"];
  labelKey: string;
  unit: "" | "g" | "mg";
  /** Sub-figures are indented under the total they break down. */
  indent?: boolean;
}> = [
  { key: "kcal", labelKey: "nutrition.kcal", unit: "" },
  { key: "protein_g", labelKey: "nutrition.protein", unit: "g" },
  { key: "carbs_g", labelKey: "nutrition.carbs", unit: "g" },
  { key: "sugar_g", labelKey: "nutrition.sugars", unit: "g", indent: true },
  { key: "fat_g", labelKey: "nutrition.fat", unit: "g" },
  { key: "saturated_fat_g", labelKey: "nutrition.saturates", unit: "g", indent: true },
  { key: "fibre_g", labelKey: "nutrition.fibre", unit: "g" },
  { key: "sodium_mg", labelKey: "nutrition.sodium", unit: "mg" },
];

export function NutritionPanel({ recipeId, panel, status, silent, onRefresh }: {
  recipeId: string;
  panel: Panel | null;
  status: NutritionStatus;
  silent: boolean;
  /** Called when the panel changes, so the recipe query can refetch. */
  onRefresh: () => void;
}) {
  const { t } = usePrefs();
  const toast = useToast();
  const qc = useQueryClient();

  const recalc = useMutation({
    mutationFn: () => RecipesApi.estimateNutrition(recipeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["recipes", recipeId] });
      onRefresh();
    },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  // ── polling while an estimate is in flight ────────────────────────────────
  //
  // The server starts the estimate as a side effect of the GET that produced
  // `status`, so there is nothing to kick off here — only something to wait
  // for. Three seconds is comfortably longer than a round trip and comfortably
  // shorter than the ten-to-fifteen an Opus call takes, so the panel appears
  // within one tick of being ready without hammering the route.
  //
  // Bounded by the status itself: the moment the server says 'ready' or
  // 'failed' the effect tears its interval down. A crashed estimate lands on
  // 'failed' via the claim timeout rather than polling forever.
  useEffect(() => {
    if (status !== "pending") return;
    const timer = setInterval(onRefresh, 3000);
    return () => clearInterval(timer);
  }, [status, onRefresh]);

  // The whole feature, off. Not a placeholder, not a disabled state — nothing.
  if (silent || status === "hidden") return null;

  // Never estimated and nothing running: this is a household with no API key,
  // or one over its monthly cap. Silence beats an explanation nobody asked for.
  if (status === "none" && !panel) return null;

  const rows = panel
    ? FIELDS.filter((f) => typeof panel.per_serving[f.key] === "number")
    : [];

  return (
    <section aria-labelledby="nutrition-heading">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        {/* "Per serving" is IN the heading, not a footnote under it. See the
            note at the top of this file — the servings stepper is a few
            hundred pixels up the same page. */}
        <h2 id="nutrition-heading" className="type-section">
          {t("nutrition.title")}
        </h2>
        <span className="type-meta">
          {panel?.source === "source"
            ? t("nutrition.published")
            : t("nutrition.estimated")}
        </span>
      </div>

      {status === "pending" && !panel && (
        <div className="flex items-center gap-2 py-3 text-sm text-ink-3" role="status">
          <Spinner /> {t("nutrition.working")}
        </div>
      )}

      {rows.length > 0 && (
        <>
          {/* Rules rather than a card, matching the ingredients list directly
              above it. A bordered box here would read as a widget bolted onto
              a page that is otherwise prose. */}
          <dl className="divide-y divide-rule-soft border-y border-rule-soft">
            {rows.map((f) => (
              <div key={f.key} className="flex items-baseline justify-between gap-4 py-2">
                <dt className={`text-sm ${f.indent ? "pl-4 text-ink-3" : "text-ink-2"}`}>
                  {t(f.labelKey)}
                </dt>
                <dd className="tabular-nums text-sm font-medium">
                  {panel!.per_serving[f.key]}
                  {f.unit ? ` ${f.unit}` : ""}
                </dd>
              </div>
            ))}
          </dl>

          {/* Often the most useful line in the panel. "Assumes unsalted stock;
              a stock cube would add roughly 400mg" tells a cook something they
              can act on, which a sodium figure on its own does not. */}
          {panel?.note && (
            <p className="type-meta mt-2">{panel.note}</p>
          )}

          <p className="type-meta mt-2">
            {panel?.source === "source"
              ? t("nutrition.disclaimer.published")
              : t("nutrition.disclaimer.estimated")}
          </p>
        </>
      )}

      {status === "failed" && !panel && (
        <p className="type-meta py-2">{t("nutrition.failed")}</p>
      )}

      <div className="mt-2 flex items-center gap-2">
        <button
          className="btn-ghost text-xs"
          onClick={() => recalc.mutate()}
          disabled={recalc.isPending || status === "pending"}
        >
          {recalc.isPending ? <Spinner className="h-3 w-3" /> : null}
          {panel ? t("nutrition.recalculate") : t("nutrition.calculate")}
        </button>
        {/* Shown only when the estimate itself is shaky, rather than as
            permanent chrome. A confidence badge on every panel trains people
            to stop reading it. */}
        {panel?.confidence === "low" && (
          <span className="type-meta">{t("nutrition.lowConfidence")}</span>
        )}
      </div>
    </section>
  );
}
